import React, { useEffect, useState } from "react";
import {
  CrimeReference,
  CrimeTypeForInvestigationType,
  FILE_URL,
  Investigation,
  InvestigationFile,
  InvestigationFileBy,
  InvestigationFileStatus,
  ProsecutorDecisionStatus,
  ProsecutorDecisionType,
  Roles,
} from "types";
import {
  DateView,
  errorHandler,
  errorHandlerOnForm,
  hasRole,
} from "utils/helper";
// import InvestigationFileDecision from "./InvestigationFileDecision";
import DocumentViewerModal from "components/DocumentViewer";
import MergeCases from "components/MergeCases";
import DoesntChargeRequest from "components/RequestDoesntCharge";
import DoubtfulRequest from "components/RequestDoubtful";
import RequestInstituteOfCharge from "components/RequestInsituteOfCharge";
import PettyOffeneRequest from "components/RequestPettyOffence";
import RequestSuspectToWitnessModal from "components/RequestSuspecToWitness";
import ResumeInvestigation from "components/ResumeInvestigation";
import SuspendInvestigation from "components/SuspendInvestigation";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import InvestigationFileForm from "pages/InvestigationFileList/modal/InvestigationFileForm";
import RequestPrivateProsecutiion from "pages/PrivateProsecution/fragment/ReqeustPrivateProsecution";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  fetchCrimeReferences,
  fetchInvestigation,
} from "services/incidentService";
import {
  addCoordinatorComment,
  editReceivingInvestigationFile2,
} from "services/prosecutorPoliceService";
import { actionListIcon, CheckIcon } from "utils/constant";
import FurtherInvestigationForm from "../modal/FurtherInvestigationForm";

type props = {
  file: InvestigationFile;
  setRefetch: React.Dispatch<React.SetStateAction<boolean>>;
};

const InvestigationInformationTab: React.FC<props> = ({ file, setRefetch }) => {
  const [show, setShow] = useState(true);
  const { loginData } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showTooltip } = useTooltip();

  const [incident, setIncident] = useState<CrimeReference | null>(null);
  const [investigation, setInvestigation] = useState<Investigation>();
  const isClerk = hasRole(loginData?.roles, [
    Roles.clerk,
    Roles.economicClerk,
    Roles.corruptionClerk,
  ]);
  const isCoordinator = hasRole(loginData?.roles, [
    Roles.coordinator,
    Roles.corruptionCoordinator,
    Roles.economicCoordinator,
    Roles.variousCoordinator,
  ]);
  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const [openEditInvestigationFile, setOpenEditInvestigationFile] =
    useState(false);

  // const [fileTooltip, setFileTooltip] = useState<InvestigationFile | undefined>(
  //   undefined
  // );
  const [selectedFile, setSelectedFile] = useState<
    InvestigationFile | undefined
  >(undefined);
  const [isOpenPettyOffence, setIsOpenPettyOffence] = useState(false);
  const [isOpenPrivateProsecution, setIsOpenPrivateProsecution] =
    useState(false);
  const [isOpenDoesntCharge, setIsOpenDoesntCharge] = useState(false);
  // const [isOpenClosed, setIsOpenClosed] = useState(false);
  const [isOpenFurtherInvestigation, setIsOpenFurtherInvestigation] =
    useState(false);
  const [isOpenInstituteOfCharge, setIsOpenInstituteOfCharge] = useState(false);
  const [isOpenSuspectToWitess, setIsOpenSuspectToWitess] = useState(false);
  const [isOpenDoubtful, setIsOpenDoubtful] = useState(false);
  const [isOpenSuspend, setIsOpenSuspend] = useState(false);
  const [isOpenResume, setIsOpenResume] = useState(false);
  const [isOpenMergeCases, setIsOpenMergeCases] = useState(false);
  const [incidentIsLoading, setIncidentIsLoading] = useState(false);

  const [isRespondBackOpen, setIsRespondBackOpen] = useState(false);
  const [isForwardToOtherDepartmentOpen, setIsForwardToOtherDepartmentOpen] =
    useState(false);
  const [comment, setComment] = useState("");
  const [department, setDepartment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Suspend & Resume are both prosecutor decisions awaiting coordinator
  // approval. There is no "resume" decision type, so the two pending states
  // are told apart by is_case_suspended: a suspend is requested on an active
  // case, a resume on an already-suspended case.
  const suspensionPending =
    !file?.is_case_suspended &&
    file?.prosecutor_decision_type === "suspend" &&
    file?.prosecutor_decision_status === "pending";
  const suspensionRespondedBack =
    !file?.is_case_suspended &&
    file?.prosecutor_decision_type === "suspend" &&
    file?.prosecutor_decision_status === "revised";
  const resumePending =
    !!file?.is_case_suspended &&
    file?.prosecutor_decision_status === "pending";
  const resumeRespondedBack =
    !!file?.is_case_suspended &&
    file?.prosecutor_decision_status === "revised";
  // Once a not-to-charge decision is approved the case is closed — the
  // prosecutor can no longer make new decisions.
  const doesntChargeApproved =
    file?.prosecutor_decision_type === "decision_not_to_charge" &&
    file?.prosecutor_decision_status === "approved";

  const [suspendApproveLoading, setSuspendApproveLoading] = useState(false);
  const [suspendRespondOpen, setSuspendRespondOpen] = useState(false);
  const [suspendComment, setSuspendComment] = useState("");
  const [showPreviousDecisionFiles, setShowPreviousDecisionFiles] =
    useState(false);

  // Prosecutor decision files (newest first). Uploaded `decision_file`s are
  // stored as DMS documents with document_type "prosecutor_decision".
  const decisionFiles = (file?.documents || [])
    .filter((d) => d?.document_type === "prosecutor_decision")
    .slice()
    .sort(
      (a, b) =>
        new Date(b?.created_at || "").getTime() -
        new Date(a?.created_at || "").getTime(),
    );
  const latestDecisionFile = decisionFiles[0];
  const previousDecisionFiles = decisionFiles.slice(1);

  // Decision file display: latest prominently, previous ones in an accordion.
  const renderDecisionFiles = () => {
    if (!latestDecisionFile && !file?.decision_file_info?.file_url) return null;
    return (
      <div className="mt-3">
        <p className="text-xs font-medium text-gray-500 mb-1">
          {t("decision_file")}
        </p>
        {latestDecisionFile ? (
          <div className="space-y-2">
            <DocumentViewerModal
              documents={[latestDecisionFile] as any}
              baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.RECEIVING_INVESTIGATION}`}
              layout="list"
              hideEmptyFile={true}
              label={previousDecisionFiles.length > 0 ? t("latest_file") : ""}
            />
            {previousDecisionFiles.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowPreviousDecisionFiles((v) => !v)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                >
                  <span className="text-xs font-medium text-gray-700">
                    {t("previous_files")} ({previousDecisionFiles.length})
                  </span>
                  <svg
                    className={`w-4 h-4 shrink-0 text-gray-500 transition-transform ${
                      showPreviousDecisionFiles ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {showPreviousDecisionFiles && (
                  <div className="px-3 py-3 border-t border-gray-200">
                    <DocumentViewerModal
                      documents={previousDecisionFiles as any}
                      baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.RECEIVING_INVESTIGATION}`}
                      layout="list"
                      hideEmptyFile={true}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <a
            href={file?.decision_file_info?.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors text-sm"
          >
            📄 {t("view")}
          </a>
        )}
      </div>
    );
  };

  const approveSuspension = () => {
    setSuspendApproveLoading(true);
    editReceivingInvestigationFile2({
      id: file?.id,
      is_case_suspended: true,
      status: "case_suspended",
      previous_status: file?.status ?? null,
      prosecutor_decision_status: "approved",
    })
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        setRefetch((prev) => !prev);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setSuspendApproveLoading(false));
  };

  const approveResume = () => {
    setSuspendApproveLoading(true);
    editReceivingInvestigationFile2({
      id: file?.id,
      is_case_suspended: false,
      status: file?.previous_status,
      prosecutor_decision_status: "approved",
    })
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        setRefetch((prev) => !prev);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setSuspendApproveLoading(false));
  };

  // Shared respond-back for both suspend and resume decisions.
  const respondBackSuspension = async () => {
    if (!suspendComment.trim()) {
      showTooltip(t("respond_back"), "error");
      return;
    }
    setSuspendApproveLoading(true);
    try {
      await addCoordinatorComment({
        investigation_file: file?.id,
        comment_text: suspendComment,
        created_by: loginData?.full_name,
      });
      await editReceivingInvestigationFile2({
        id: file?.id,
        prosecutor_decision_status: "revised",
      });
      showTooltip(t("successfully_submitted"), "success");
      setSuspendRespondOpen(false);
      setSuspendComment("");
      setRefetch((prev) => !prev);
    } catch (error: any) {
      showTooltip(
        error?.response?.data?.detail || errorHandlerOnForm(error),
        "error",
      );
    } finally {
      setSuspendApproveLoading(false);
    }
  };

  useEffect(() => {
    if (file) {
      setIncidentIsLoading(true);
      fetchCrimeReferences()
        ?.then((data) => {
          if (data) {
            setIncident(
              data?.find(
                (item) =>
                  item.Incident_Registration_number ===
                  file?.incident_registration_number,
              ) || null,
            );
          }
        })
        .finally(() => setIncidentIsLoading(false));
      fetchInvestigation()?.then((data) => {
        if (data) {
          setInvestigation(
            data?.find(
              (a) =>
                a?.CR_number === file?.crime_reference_number &&
                a?.Incident_Registration_number ===
                  file?.incident_registration_number,
            ),
          );
        }
      });
    }
  }, [file]);

  const hideAll = () => {
    setIsOpenPettyOffence(false);
    setIsOpenPrivateProsecution(false);
    setIsOpenDoesntCharge(false);
    setIsOpenFurtherInvestigation(false);
    setIsOpenInstituteOfCharge(false);
    setIsOpenSuspectToWitess(false);
    setIsOpenDoubtful(false);
    setIsOpenSuspend(false);
    setIsOpenResume(false);
    setIsOpenMergeCases(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row justify-between gap-6 w-full">
        <div className="w-full">
          <div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              {/* <div className="flex items-center mt-4 w-[678px]">
                <p
                  className="text-[#9D0303] font-[Poppins] font-[600] text-lg cursor-pointer"
                  onClick={() => setShow(!show)}
                >
                  About Investigation File
                </p>
                <hr className="flex-1 border-t border-[#E9E9E9] ml-4" />
              </div> */}
              <div className={show ? "block" : "hidden"}>
                <div className="px-4 mt-4">
                  <div className="grid grid-cols-3 gap-4 w-full">
                    {[
                      {
                        property: t("investigation_file_by"),
                        value: file?.investigationfileby
                          ? InvestigationFileBy[file?.investigationfileby]
                          : t("na"),
                      },
                      {
                        property: t("schedule_file_receiving_date"),
                        value:
                          DateView(file?.schedule_file_receiving_date) ||
                          t("na"),
                      },
                      {
                        property: t("file_receiving_date"),
                        value: DateView(file?.receiving_date) || t("na"),
                      },
                      {
                        property: t("crime_reference_number"),
                        value: file?.crime_reference_number || t("na"),
                      },
                      {
                        property: t("incident_registration_number"),
                        value: file?.incident_registration_number || t("na"),
                      },
                      {
                        property: t("prosecutor_registration_number"),
                        value: file?.prosecutor_registration_number || t("na"),
                      },
                      {
                        property: t("crime_type"),
                        value: file?.crime_type
                          ? CrimeTypeForInvestigationType[file?.crime_type]
                          : t("na"),
                      },
                      {
                        property: t("assign_clerk"),
                        value: file?.assign_clerk || t("na"),
                      },
                      {
                        property: t("assign_prosecutor"),
                        value: file?.assign_prosecutor || t("na"),
                      },
                      {
                        property: t("date_of_assign_prosecutor"),
                        value: file?.prosecutor_assignment_date
                          ? DateView(file?.prosecutor_assignment_date)
                          : t("na"),
                      },
                      {
                        property: t("prosecutor_decision_type"),
                        value:
                          ProsecutorDecisionType[
                            file?.prosecutor_decision_type as keyof typeof ProsecutorDecisionType
                          ] || t("na"),
                      },
                      {
                        property: t("prosecutor_decision_status"),
                        value:
                          ProsecutorDecisionStatus[
                            file?.prosecutor_decision_status as keyof typeof ProsecutorDecisionStatus
                          ] || t("na"),
                      },
                      {
                        property: t("status"),
                        value:
                          InvestigationFileStatus[
                            file?.status as keyof typeof InvestigationFileStatus
                          ] || t("na"),
                      },
                    ]?.map((info, index) => {
                      if (
                        info?.property === t("investigation_file_by") &&
                        file?.investigationfileby === ("pending" as any)
                      ) {
                        return null;
                      }
                      return (
                        <div key={index}>
                          <p className="text-sm font-medium text-red-600">
                            {info.property}
                          </p>
                          <p className="text-sm font-medium text-gray-900">
                            {info.value}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <br />

              {file?.documents && (
                <DocumentViewerModal
                  documents={
                    (file?.documents || []).filter(
                      (d) => d?.document_type !== "prosecutor_decision",
                    ) as any
                  }
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.RECEIVING_INVESTIGATION}`}
                  layout="list"
                  hide={false}
                  label=""
                  hideEmptyFile={true}
                />
              )}

              {/* Prosecutor decision file — latest + previous accordion */}
              {decisionFiles.length > 0 && (
                <div className="px-4 mt-4">
                  <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                    <p className="text-sm font-semibold text-gray-700">
                      {t("prosecutor_decision")}
                    </p>
                    {renderDecisionFiles()}
                  </div>
                </div>
              )}

              {/* suspend reason and date */}
              {file?.case_suspension_reason && (
                <div className="px-4 mt-4">
                  <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                    <p className="text-sm font-semibold text-[#9D0303] mb-3">
                      {t("case_suspension_resume")}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div className="p-3 bg-white rounded">
                        <dt className="text-xs text-gray-400">
                          {t("suspension_date")}
                        </dt>
                        <dd className="mt-1 font-medium text-gray-800">
                          {DateView(file?.case_suspension_date) ?? "-"}
                        </dd>
                      </div>

                      <div className="p-3 bg-white rounded">
                        <dt className="text-xs text-gray-400">
                          {t("suspension_reason")}
                        </dt>
                        <dd className="mt-1 font-medium text-gray-800 break-words">
                          {file?.case_suspension_reason ?? "-"}
                        </dd>
                      </div>

                      {file?.case_resume_reason && (
                        <div className="p-3 bg-white rounded">
                          <dt className="text-xs text-gray-400">
                            {t("resume_date")}
                          </dt>
                          <dd className="mt-1 font-medium text-gray-800">
                            {DateView(file?.case_resume_date) ?? "-"}
                          </dd>
                        </div>
                      )}
                      {file?.case_resume_reason && (
                        <div className="p-3 bg-white rounded">
                          <dt className="text-xs text-gray-400">
                            {t("resume_reason")}
                          </dt>
                          <dd className="mt-1 font-medium text-gray-800 break-words">
                            {file?.case_resume_reason ?? "-"}
                          </dd>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* {!isClerk &&
              file?.status !== "assigned_to_clerk" &&
              file?.assign_prosecutor && (
                <ProsecutorFeedback
                  file={file}
                  setRefetch={(refetch) =>
                    setRefetch((prev) => (refetch ? !prev : prev))
                  }
                />
              )} */}
          </div>
        </div>
        <div className="w-full lg:w-[35%]">
          {/* Take Action button with dropdown list */}
          {isProsecutor &&
            !doesntChargeApproved &&
            (file?.status === "assigned_to_prosecutor" ||
              file?.status !== "case_terminated_by_court") && (
              <div>
                {/* Coordinator responded back on the prosecutor's suspension */}
                {(suspensionRespondedBack || resumeRespondedBack) && (
                  <div className="bg-white rounded-xl shadow-sm border border-amber-200 p-6 w-full mb-4">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="w-1.5 h-6 rounded-full bg-amber-500" />
                      <h3 className="text-base font-semibold text-gray-900">
                        {t(
                          resumeRespondedBack
                            ? "case_resume_request"
                            : "case_suspension_request",
                        )}
                      </h3>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-200">
                        ↩️ {t("responded_back")}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      {t(
                        resumeRespondedBack
                          ? "resume_responded_back_hint"
                          : "suspension_responded_back_hint",
                      )}
                    </p>
                    {file?.cordinator_comments &&
                      file.cordinator_comments.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                            {t("coordinator_comments")}
                          </p>
                          {file.cordinator_comments
                            .slice()
                            .reverse()
                            .map((c, index) => (
                              <div
                                key={index}
                                className="rounded-lg border border-gray-100 bg-gray-50 p-3"
                              >
                                <p className="text-sm text-gray-800 break-words">
                                  {c?.comment_text}
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {c?.created_by}
                                  {c?.created_at
                                    ? ` • ${new Date(
                                        c.created_at,
                                      ).toLocaleString()}`
                                    : ""}
                                </p>
                              </div>
                            ))}
                        </div>
                      )}
                  </div>
                )}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 w-full">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-red-600">
                        {t("decision_of_public_prosecutor")}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {t("select_an_action")}
                      </p>
                    </div>

                    {!file?.is_case_suspended ? (
                      <div className="flex flex-col divide-y divide-gray-100">
                        {/* <button
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3"
                          onClick={() => {
                            setSelectedFile(file);
                            setIsOpenPettyOffence(true);
                          }}
                        >
                          {actionListIcon}
                          <span className="font-medium">Petty Offence</span>
                        </button> */}

                        <button
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3"
                          onClick={() => {
                            setSelectedFile(file);
                            hideAll();
                            setIsOpenPrivateProsecution(true);
                          }}
                        >
                          {actionListIcon}
                          <span className="font-medium">
                            {t("private_prosecution")}
                          </span>
                        </button>

                        <button
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3"
                          onClick={() => {
                            setSelectedFile(file);
                            hideAll();
                            setIsOpenDoesntCharge(true);
                          }}
                        >
                          {actionListIcon}
                          <span className="font-medium">
                            {t("doesnt_institute_charge")}
                          </span>
                        </button>

                        <button
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3"
                          onClick={() => {
                            setSelectedFile(file);
                            hideAll();
                            setIsOpenFurtherInvestigation(true);
                          }}
                        >
                          {actionListIcon}
                          <span className="font-medium">
                            {t("additional_information")}
                          </span>
                        </button>

                        <button
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3"
                          onClick={() => {
                            setSelectedFile(file);
                            hideAll();
                            setIsOpenInstituteOfCharge(true);
                          }}
                        >
                          {actionListIcon}
                          <span className="font-medium">
                            {t("institute_charge")}
                          </span>
                        </button>

                        <button
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3"
                          onClick={() => {
                            setSelectedFile(file);
                            hideAll();
                            setIsOpenSuspectToWitess(true);
                          }}
                        >
                          {actionListIcon}
                          <span className="font-medium">
                            {t("suspect_to_witness_tab")}
                          </span>
                        </button>

                        <button
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3"
                          onClick={() => {
                            setSelectedFile(file);
                            hideAll();
                            setIsOpenDoubtful(true);
                          }}
                        >
                          {actionListIcon}
                          <span className="font-medium">{t("doubtful")}</span>
                        </button>

                        {!suspensionPending && (
                          <button
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3"
                            onClick={() => {
                              setSelectedFile(file);
                              hideAll();
                              setIsOpenSuspend(true);
                            }}
                          >
                            {actionListIcon}
                            <span className="font-medium">{t("suspend")}</span>
                          </button>
                        )}
                        {suspensionPending && (
                          <div className="px-4 py-3 flex items-center gap-3 text-amber-700 bg-amber-50 rounded-lg">
                            <span>⏳</span>
                            <span className="text-sm font-medium">
                              {t("suspension_pending_approval")}
                            </span>
                          </div>
                        )}

                        {/* <button
                          className="w-full text-left px-4 py-3 hover:bg-[#F8F8F8] transition flex items-center gap-3 rounded-b-xl"
                          onClick={() => {
                            setSelectedFile(file);
                            hideAll();
                            setIsOpenMergeCases(true);
                          }}
                        >
                          {actionListIcon}
                          <span className="font-medium">Merge Cases</span>
                        </button> */}
                      </div>
                    ) : resumePending ? (
                      <div className="px-4 py-3 flex items-center gap-3 text-amber-700 bg-amber-50 rounded-lg">
                        <span>⏳</span>
                        <span className="text-sm font-medium">
                          {t("resume_pending_approval")}
                        </span>
                      </div>
                    ) : (
                      <button
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3"
                        onClick={() => {
                          setSelectedFile(file);
                          hideAll();
                          setIsOpenResume(true);
                        }}
                      >
                        {actionListIcon}
                        <span className="font-medium">{t("resume")}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          {isClerk && (
            <div className="flex flex-col gap-5">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="bg-red-600 text-white px-4 py-2 inline-block rounded-b-md mb-4">
                  {t("incident")}
                </div>

                <div className="flex gap-3 items-center mb-4">
                  <div className="">{CheckIcon}</div>
                  <p className="text-gray-600">
                    {t("manage_incident_details_and_registration")}
                  </p>
                </div>
                <button
                  className="w-full bg-gray-900 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors"
                  onClick={() => {
                    navigate(
                      incident
                        ? "/record-incident/" + incident?.uu_id
                        : "/incident-register",
                      {
                        state: { file, incident },
                      },
                    );
                  }}
                  disabled={incidentIsLoading}
                >
                  {incidentIsLoading
                    ? t("loading")
                    : incident
                      ? t("record_incident")
                      : t("register_incident")}
                </button>
              </div>
            </div>
          )}
          {/* Coordinator review of a pending case-suspension decision */}
          {isCoordinator && suspensionPending && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 w-full mb-5">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-1.5 h-6 rounded-full bg-amber-500" />
                <h3 className="text-base font-semibold text-gray-900">
                  {t("case_suspension_request")}
                </h3>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                  {t("status_pending")}
                </span>
              </div>

              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-gray-500 mb-1">
                    {t("suspension_reason")}
                  </p>
                  <p className="text-sm text-gray-800 break-words">
                    {file?.case_suspension_reason ?? "-"}
                  </p>
                  {renderDecisionFiles()}
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-gray-500 mb-1">
                    {t("suspension_date")}
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {DateView(file?.case_suspension_date) || "-"}
                  </p>
                </div>
              </div>

              {!suspendRespondOpen ? (
                <div className="flex flex-wrap gap-3 mt-5 pt-5 border-t border-gray-200">
                  <button
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                    disabled={suspendApproveLoading}
                    onClick={() => setSuspendRespondOpen(true)}
                  >
                    {t("respond_back")}
                  </button>
                  <button
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                    disabled={suspendApproveLoading}
                    onClick={approveSuspension}
                  >
                    {suspendApproveLoading ? t("submitting") : t("approve")}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-3 mt-5 pt-5 border-t border-gray-200">
                  <textarea
                    className="w-full p-3 border border-gray-200 rounded-lg"
                    placeholder={t("respond_back")}
                    rows={4}
                    value={suspendComment}
                    onChange={(e) => setSuspendComment(e.target.value)}
                  />
                  <div className="flex gap-3">
                    <button
                      className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
                      disabled={suspendApproveLoading}
                      onClick={respondBackSuspension}
                    >
                      {suspendApproveLoading
                        ? t("submitting")
                        : t("submit_response")}
                    </button>
                    <button
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                      onClick={() => {
                        setSuspendRespondOpen(false);
                        setSuspendComment("");
                      }}
                    >
                      {t("cancel")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Coordinator review of a pending case-resume decision */}
          {isCoordinator && resumePending && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 w-full mb-5">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-1.5 h-6 rounded-full bg-emerald-500" />
                <h3 className="text-base font-semibold text-gray-900">
                  {t("case_resume_request")}
                </h3>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                  {t("status_pending")}
                </span>
              </div>

              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-gray-500 mb-1">
                    {t("resume_reason")}
                  </p>
                  <p className="text-sm text-gray-800 break-words">
                    {file?.case_resume_reason ?? "-"}
                  </p>
                  {renderDecisionFiles()}
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-gray-500 mb-1">
                    {t("resume_date")}
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {DateView(file?.case_resume_date) || "-"}
                  </p>
                </div>
              </div>

              {!suspendRespondOpen ? (
                <div className="flex flex-wrap gap-3 mt-5 pt-5 border-t border-gray-200">
                  <button
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                    disabled={suspendApproveLoading}
                    onClick={() => setSuspendRespondOpen(true)}
                  >
                    {t("respond_back")}
                  </button>
                  <button
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                    disabled={suspendApproveLoading}
                    onClick={approveResume}
                  >
                    {suspendApproveLoading ? t("submitting") : t("approve")}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-3 mt-5 pt-5 border-t border-gray-200">
                  <textarea
                    className="w-full p-3 border border-gray-200 rounded-lg"
                    placeholder={t("respond_back")}
                    rows={4}
                    value={suspendComment}
                    onChange={(e) => setSuspendComment(e.target.value)}
                  />
                  <div className="flex gap-3">
                    <button
                      className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
                      disabled={suspendApproveLoading}
                      onClick={respondBackSuspension}
                    >
                      {suspendApproveLoading
                        ? t("submitting")
                        : t("submit_response")}
                    </button>
                    <button
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                      onClick={() => {
                        setSuspendRespondOpen(false);
                        setSuspendComment("");
                      }}
                    >
                      {t("cancel")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {isCoordinator &&
            file?.status === "forward_to_coordinator" &&
            !file?.assign_clerk && (
              <div className="flex flex-col gap-5 mb-5">
                {!isRespondBackOpen && !isForwardToOtherDepartmentOpen && (
                  <div className="flex flex-row sm:flex-row gap-3 sm:gap-5">
                    <button
                      className="w-full sm:w-auto bg-green-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                      disabled={isSubmitting}
                      onClick={async () => {
                        setIsSubmitting(true);
                        try {
                          await editReceivingInvestigationFile2({
                            id: file?.id,
                            org_id: loginData?.org_unit,
                            status: "assigned_to_clerk",
                          });
                          setRefetch((prev) => !prev);
                          showTooltip(
                            t("successfully_forwarded_to_clerk"),
                            "success",
                          );
                        } catch (error: any) {
                          showTooltip(
                            error?.response?.data ||
                              errorHandlerOnForm(error) ||
                              errorHandler(error) ||
                              t("something_went_wrong_please_try_again"),
                          );
                          setIsSubmitting(false);
                        } finally {
                          setIsSubmitting(false);
                        }
                      }}
                    >
                      {t("forward_to_clerk")}
                    </button>
                    <button
                      className="w-full sm:w-auto bg-gray-900 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors"
                      onClick={() => {
                        setIsForwardToOtherDepartmentOpen(true);
                      }}
                    >
                      {t("forward_to_other_department")}
                    </button>
                    <button
                      className="w-full sm:w-auto bg-red-900 text-white py-2 px-4 rounded-lg font-medium hover:bg-red-800 transition-colors"
                      onClick={() => {
                        setIsRespondBackOpen(true);
                      }}
                    >
                      {t("respond_back")}
                    </button>
                  </div>
                )}

                {isRespondBackOpen && (
                  <div className="flex flex-col gap-3">
                    <textarea
                      className="w-full p-4 border border-gray-200 rounded-lg"
                      placeholder={t("respond_back")}
                      rows={4}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                    ></textarea>
                    <div className="flex flex-row gap-5">
                      <button
                        className="w-full bg-gray-900 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors"
                        onClick={async () => {
                          setIsSubmitting(true);
                          try {
                            const response = await addCoordinatorComment({
                              investigation_file: file?.id,
                              comment_text: comment,
                              created_by: loginData?.full_name,
                            });

                            if (response) {
                              await editReceivingInvestigationFile2({
                                id: file?.id,
                                org_id: loginData?.org_unit,
                                status: "responddback_to_police",
                              });
                              showTooltip(
                                t("successfully_submitted"),
                                "success",
                              );
                              navigate("/investigation-files");
                            }
                          } catch (error) {
                            setIsSubmitting(false);
                          }
                        }}
                      >
                        {isSubmitting ? t("submitting") : t("submit_response")}
                      </button>
                      <button
                        className="w-full bg-gray-900 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors"
                        onClick={() => setIsRespondBackOpen(false)}
                      >
                        {t("cancel")}
                      </button>
                    </div>
                  </div>
                )}

                {isForwardToOtherDepartmentOpen && isCoordinator && (
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col mt-4">
                      <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                        {t("select_department")}
                      </label>
                      <select
                        className="border capitalize border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                        value={department}
                        onChange={(e) => setDepartment(e.target.value)}
                      >
                        <option value={""}>{t("select_department")}</option>
                        {Object.entries(CrimeTypeForInvestigationType).map(
                          ([key, value]) => (
                            <option key={key} value={key}>
                              {value}
                            </option>
                          ),
                        )}
                      </select>
                    </div>

                    <div className="flex flex-row gap-5">
                      <button
                        className="w-full bg-gray-900 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors"
                        onClick={async () => {
                          setIsSubmitting(true);
                          try {
                            const response =
                              await editReceivingInvestigationFile2({
                                id: file?.id,
                                crime_type: department as any,
                                is_forwared_horizontally: true,
                              });

                            if (response) {
                              showTooltip(
                                t("successfully_submitted"),
                                "success",
                              );
                              // setComment("");
                              // setIsSubmitting(false);
                              // setIsForwardToOtherDepartmentOpen(false);
                              // setRefetch((prev) => !prev);
                              navigate("/investigation-files");
                            }
                          } catch (error) {
                            setIsSubmitting(false);
                          }
                        }}
                      >
                        {isSubmitting ? t("submitting") : t("submit_response")}
                      </button>
                      <button
                        className="w-full bg-gray-900 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors"
                        onClick={() => setIsRespondBackOpen(false)}
                      >
                        {t("cancel")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

          {(isCoordinator || loginData?.is_police) &&
            file?.cordinator_comments && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 ">
                <div className="bg-red-600 text-white px-4 py-2 inline-block rounded-b-md mb-4">
                  {t("coordinator_comments")}
                </div>

                <div className="flex flex-col gap-4 mt-4 justify-between h-[70vh] w-full">
                  <div className="flex flex-col overflow-y-auto gap-2">
                    {file?.cordinator_comments?.map((comment, index) => (
                      <div
                        key={index}
                        className={`${
                          index % 2 === 0 ? "bg-[#E9E9E9]" : "bg-white"
                        } px-6 py-3`}
                        style={{
                          overflowWrap: "break-word",
                        }}
                      >
                        <p className="text-[#E90102] font-normal text-sm">
                          {t("commented_date")}
                        </p>
                        <p className="text-black font-normal text-sm">
                          {comment?.created_at &&
                            new Date(comment?.created_at).toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-600 mt-3">
                          {comment?.comment_text}
                        </p>
                        <p className="text-sm text-gray-600 mt-3s">
                          {t("commented_by")}: {comment?.created_by}
                        </p>
                      </div>
                    ))}

                    {loginData?.is_police &&
                      file?.status === "responddback_to_police" && (
                        <div className="flex flex-row gap-5">
                          <button
                            className="w-full bg-gray-900 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors"
                            onClick={async () => {
                              setOpenEditInvestigationFile(true);
                            }}
                          >
                            {t("edit_investigation_file")}
                          </button>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            )}
          {/* {!isClerk && (
            <div className="mt-4 min-w-[500px] flex flex-col gap-5">
              {file?.petty_offence_decison && (
                <PettyOffenceApprovalCard
                  file={file}
                  onApprove={function (): void {
                    editReceivingInvestigationFile2({
                      id: file?.id,
                      prosecutor_decision_type: "petty_offence",
                      prosecutor_decision_status: "approved",
                    })
                      .then(() => {
                        showTooltip(t("successfully_submitted"), "success");
                        setRefetch(true);
                      })
                      .catch((err) => {
                        showTooltip(
                          errorHandlerOnForm(err?.response?.data),
                          "error"
                        );
                      });
                  }}
                  onReject={function (): void {
                    editReceivingInvestigationFile2({
                      id: file?.id,
                      prosecutor_decision_type: "petty_offence",
                      prosecutor_decision_status: "rejected",
                    })
                      .then(() => {
                        showTooltip(t("successfully_submitted"), "success");
                        setRefetch(true);
                      })
                      .catch((err) => {
                        showTooltip(
                          errorHandlerOnForm(err?.response?.data),
                          "error"
                        );
                      });
                  }}
                />
              )}
            </div>
          )} */}
        </div>
      </div>
      {isOpenInstituteOfCharge && (
        <RequestInstituteOfCharge
          file={selectedFile}
          investigationFiles={
            file?.status === "assigned_to_prosecutor" ? [file] : []
          }
          onCancel={() => {
            setIsOpenInstituteOfCharge(false);
            // setFileTooltip(undefined);
          }}
          isOpen={isOpenInstituteOfCharge}
        />
      )}
      {isOpenDoubtful && selectedFile && (
        <DoubtfulRequest
          file={selectedFile}
          onCancel={() => {
            setIsOpenDoubtful(false);
            // setFileTooltip(undefined);
          }}
        />
      )}
      {isOpenSuspend && selectedFile && (
        <SuspendInvestigation
          file={selectedFile}
          onClose={(refetch) => {
            setIsOpenSuspend(false);
            // setFileTooltip(undefined);
            if (refetch) {
              setRefetch((prev) => !prev);
            }
          }}
        />
      )}
      {isOpenResume && selectedFile && (
        <ResumeInvestigation
          file={selectedFile}
          onClose={(refetch) => {
            setIsOpenResume(false);
            // setFileTooltip(undefined);
            if (refetch) {
              setRefetch((prev) => !prev);
            }
          }}
        />
      )}
      {isOpenSuspectToWitess && selectedFile && (
        <RequestSuspectToWitnessModal
          file={selectedFile}
          onCancel={() => {
            setIsOpenSuspectToWitess(false);
            // setFileTooltip(undefined);
          }}
        />
      )}
      {/* {isOpenClosed && selectedFile && (
        <CloseInvestigationFile
          file={selectedFile}
          setRefetch={() => setRefetch(true)}
          onCancel={() => {
            setIsOpenClosed(false);
            setFileTooltip(undefined);
            setSelectedFile(undefined);
          }}
          isOpen={isOpenClosed}
        />
      )} */}
      {isOpenPettyOffence && selectedFile && (
        <PettyOffeneRequest
          file={selectedFile}
          setRefetch={() => setRefetch((prev) => !prev)}
          onCancel={() => {
            setIsOpenPettyOffence(false);
            // setFileTooltip(undefined);
            setSelectedFile(undefined);
          }}
        />
      )}

      {isOpenPrivateProsecution && selectedFile && (
        <RequestPrivateProsecutiion
          file={selectedFile}
          setRefetch={() => setRefetch((prev) => !prev)}
          onCancel={() => {
            setIsOpenPrivateProsecution(false);
            // setFileTooltip(undefined);
            setSelectedFile(undefined);
          }}
        />
      )}

      {isOpenDoesntCharge && selectedFile && (
        <DoesntChargeRequest
          file={selectedFile}
          setRefetch={() => setRefetch((prev) => !prev)}
          onCancel={() => {
            setIsOpenPettyOffence(false);
            // setFileTooltip(undefined);
            setSelectedFile(undefined);
          }}
        />
      )}
      {isOpenFurtherInvestigation && selectedFile && (
        <FurtherInvestigationForm
          isOpen={isOpenFurtherInvestigation}
          action={"Add"}
          file={selectedFile}
          furtherInvestigation={undefined}
          onClose={(refetch) => {
            setIsOpenFurtherInvestigation(false);

            console.log("refetch", refetch);
            if (refetch) {
              setRefetch((prev) => !prev);
            }
          }}
        />
      )}
      {isOpenMergeCases && selectedFile && (
        <MergeCases
          file={selectedFile}
          onCancel={() => {
            setIsOpenMergeCases(false);
            // setFileTooltip(undefined);
          }}
        />
      )}

      {openEditInvestigationFile && file && (
        <InvestigationFileForm
          action={"Edit"}
          investigationFile={file}
          isOpen={openEditInvestigationFile}
          onClose={() => setOpenEditInvestigationFile(false)}
        />
      )}
    </div>
  );
};

export default InvestigationInformationTab;
